import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NtApiWorkOrder } from '../../../api/netsuite/types/work-order';
import { ApiNtWorkOrderService } from '../../../api/netsuite/routes/work-order.service';
import { BomDetailsDialogData } from './bom-details.component';
import { InspectionAssemblyItem } from '../../../api/elite/types/inspection-assembly-item';
import { ApiRouteInspectionService } from '../../../api/elite/routes/inspection.service';
import { ApiNsWorkOrderDto } from '../../../api/netsuite/dto/work-order.dto';
import { ApiRouteLotNumberService } from '../../../api/elite/routes/lot-number.service';
import { LotNumberBom } from '../../../api/elite/types/lot-number';
import { ApiPlasticMachineService } from 'src/app/api/elite/routes/plastic-machine.service';
import { BomAssemblyItem } from 'src/app/api/elite/types/bom-assembly';
import { ApiNtAssemblyItemService } from '../../../api/netsuite/routes/assembly-item.service';

type NetsuiteData = { bomData: ApiNsWorkOrderDto['bom']['result'] } & {
  workOrderData: NtApiWorkOrder;
};
type NetsuiteOnlyBom = ApiNsWorkOrderDto['bom']['result'] & {
  bomLots: LotNumberBom[];
};
type ResData = NetsuiteData & {
  bomLots: LotNumberBom[];
};
export type BomDataResponse = NetsuiteOnlyBom | ResData;
type ResBomData = {
  itemBom: BomDataResponse;
  linesBom: BomDataResponse;
  workOrderData: NtApiWorkOrder;
};

type BomData = {
  itemBom: ApiNsWorkOrderDto['bom']['result'] | NetsuiteData;
  linesBom: ApiNsWorkOrderDto['bom']['result'] | NetsuiteData;
};

@Injectable({
  providedIn: 'root',
})
export class BomDetailsApiService {
  private readonly netsuiteWorkOrderRouter = inject(ApiNtWorkOrderService);
  private readonly apiInspectionRouter = inject(ApiRouteInspectionService);
  private readonly apiLotNumberRouter = inject(ApiRouteLotNumberService);
  private readonly apiNetsuiteItemRouter = inject(ApiNtAssemblyItemService);
  constructor() {}

  data(
    workOrder: NtApiWorkOrder,
    info: BomDetailsDialogData
  ): Observable<ResBomData> {
    let workOrderData: NtApiWorkOrder;
    return this.getWoInfo(workOrder, info).pipe(
      switchMap((data: NtApiWorkOrder) => {
        workOrderData = data;
        return this.getBomData(data);
      }),
      switchMap((res) => {
        const { itemBom, linesBom } = res;
        return this.apiLotNumberRouter
          .bomLots(workOrderData.transactionId)
          .pipe(
            map((woLot) => {
              return {
                itemBom: {
                  ...itemBom,
                  bomLots: woLot?.bomLots || [],
                },
                linesBom: {
                  ...linesBom,
                  bomLots: woLot?.bomLots || [],
                },
                workOrderData,
              };
            })
          );
      })
    );
  }

  validateBomLot(workOrderId: number, lotNumber: string): Observable<any> {
    return this.apiLotNumberRouter.validateBomLot(workOrderId, lotNumber);
  }

  inspectionSheet(itemName: string): Observable<InspectionAssemblyItem> {
    return this.apiInspectionRouter.getInspectionMeasurementData(itemName);
  }

  private getWoInfo(
    workOrder: NtApiWorkOrder,
    info: BomDetailsDialogData
  ): Observable<NtApiWorkOrder> {
    return workOrder
      ? of(workOrder)
      : this.netsuiteWorkOrderRouter.information(info.searching, info.query);
  }

  private getBomData(workOrder: NtApiWorkOrder): Observable<BomData> {
    return forkJoin({
      itemBom: this.getOnlyBom(workOrder.transactionNumber),
      linesBom: this.getLineBom(workOrder.transactionNumber),
    });
  }

  getAsemblyBomData(id: string): Observable<BomAssemblyItem[]> {
    return this.apiNetsuiteItemRouter.phantomBom(id);
  }

  private getOnlyBom(
    workOrder: string
  ): Observable<ApiNsWorkOrderDto['bom']['result']> {
    return this.netsuiteWorkOrderRouter.getBom(workOrder);
  }

  private getLineBom(
    workOrder: string
  ): Observable<ApiNsWorkOrderDto['bom']['result']> {
    return this.netsuiteWorkOrderRouter.getLinesBom(workOrder);
  }
}
